import React, { useState } from 'react';
import { Participant, LayoutMode } from '../types';
import { VideoTile } from './VideoTile';
import { Monitor, Volume2, Pin } from 'lucide-react';

interface VideoGridProps {
  localParticipant: Participant;
  localStream: MediaStream | null;
  remoteParticipants: Participant[];
  layoutMode: LayoutMode;
  pinnedId?: string | null;
  onTogglePin?: (id: string) => void;
}

export const VideoGrid: React.FC<VideoGridProps> = ({
  localParticipant,
  localStream,
  remoteParticipants,
  layoutMode,
  pinnedId: controlledPinnedId,
  onTogglePin: controlledOnTogglePin,
}) => {
  const [internalPinnedId, setInternalPinnedId] = useState<string | null>(null);

  const pinnedId = controlledPinnedId !== undefined ? controlledPinnedId : internalPinnedId;
  const handleTogglePin = (id: string) => {
    if (controlledOnTogglePin) {
      controlledOnTogglePin(id);
    } else {
      setInternalPinnedId((prev) => (prev === id ? null : id));
    }
  };

  const allParticipants = [localParticipant, ...remoteParticipants];

  // Check if any participant is explicitly pinned
  const pinnedParticipant = pinnedId ? allParticipants.find((p) => p.id === pinnedId) : null;
  // Check if any participant is sharing screen
  const screenSharer = allParticipants.find((p) => p.screen);

  // 1. PIN ACTIVE: Show pinned participant on the FULL AREA, remove other participants
  if (pinnedParticipant) {
    const isLocalPinned = pinnedParticipant.id === localParticipant.id;
    const stream = isLocalPinned ? localStream : pinnedParticipant.stream;

    return (
      <div className="flex-1 p-2 sm:p-4 h-full w-full overflow-hidden flex flex-col relative min-h-0">
        {/* Pinned Stage Header Banner */}
        <div className="absolute top-4 sm:top-6 left-4 sm:left-6 z-20 flex items-center space-x-2">
          <span className="bg-blue-600/90 backdrop-blur-md text-white text-[11px] sm:text-xs px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-xl flex items-center space-x-1.5 sm:space-x-2 shadow-xl font-medium border border-blue-400/40">
            <Pin className="w-3 sm:w-3.5 h-3 sm:h-3.5 fill-current" />
            <span className="truncate max-w-[150px] sm:max-w-xs">Pinned: {pinnedParticipant.name}</span>
          </span>
          <button
            type="button"
            onClick={() => handleTogglePin(pinnedParticipant.id)}
            className="bg-slate-900/85 hover:bg-slate-800 text-slate-300 hover:text-white text-[11px] sm:text-xs px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-xl backdrop-blur-md border border-slate-700/60 shadow-xl transition"
          >
            Show All
          </button>
        </div>

        <div className="w-full h-full flex-1 min-h-0 flex items-center justify-center">
          <VideoTile
            participant={pinnedParticipant}
            isLocal={isLocalPinned}
            stream={stream}
            isPinned={true}
            onTogglePin={() => handleTogglePin(pinnedParticipant.id)}
            className="w-full h-full shadow-2xl"
          />
        </div>
      </div>
    );
  }

  // 2. ONLY 1 OTHER PERSON EXCEPT ME: Show that remote participant on the FULL AREA
  if (remoteParticipants.length === 1 && !localParticipant.screen) {
    const singleRemote = remoteParticipants[0];
    return (
      <div className="flex-1 p-2 sm:p-4 h-full w-full overflow-hidden flex flex-col relative min-h-0">
        <div className="w-full h-full flex-1 min-h-0 flex items-center justify-center">
          <VideoTile
            participant={singleRemote}
            isLocal={false}
            stream={singleRemote.stream}
            isPinned={pinnedId === singleRemote.id}
            onTogglePin={() => handleTogglePin(singleRemote.id)}
            className="w-full h-full shadow-2xl"
          />
        </div>
      </div>
    );
  }

  // 3. SCREEN SHARE ACTIVE: Screen share takes the FULL AREA
  if (screenSharer) {
    const isLocalScreen = screenSharer.id === localParticipant.id;
    const stream = isLocalScreen ? localStream : screenSharer.stream;

    return (
      <div className="flex-1 p-2 sm:p-4 h-full w-full overflow-hidden flex flex-col relative min-h-0">
        <div className="absolute top-4 sm:top-6 left-4 sm:left-6 z-20 flex items-center space-x-2 pointer-events-none">
          <span className="bg-blue-600/90 backdrop-blur-md text-white text-[11px] sm:text-xs px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-xl flex items-center space-x-1.5 sm:space-x-2 shadow-xl font-medium border border-blue-400/40">
            <Monitor className="w-3 sm:w-3.5 h-3 sm:h-3.5" />
            <span className="truncate max-w-[150px] sm:max-w-xs">Screen: {screenSharer.name}</span>
          </span>
        </div>

        <div className="w-full h-full flex-1 min-h-0 flex items-center justify-center">
          <VideoTile
            participant={screenSharer}
            isLocal={isLocalScreen}
            stream={stream}
            isPinned={pinnedId === screenSharer.id}
            onTogglePin={() => handleTogglePin(screenSharer.id)}
            className="w-full h-full shadow-2xl"
          />
        </div>
      </div>
    );
  }

  // 4. USER IS ALONE (no remote participants yet)
  if (remoteParticipants.length === 0 && !localParticipant.screen) {
    return (
      <div className="flex-1 p-4 h-full w-full flex flex-col items-center justify-center text-center overflow-hidden min-h-0">
        <div className="w-full max-w-sm sm:max-w-lg bg-[#121622]/90 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-2xl backdrop-blur-md">
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-blue-600/20 text-blue-400 flex items-center justify-center mx-auto border border-blue-500/30">
            <Monitor className="w-6 h-6 sm:w-8 sm:h-8" />
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight">Waiting for others to join</h2>
          <p className="text-xs text-slate-400 leading-relaxed max-w-xs sm:max-w-sm mx-auto">
            Share this meeting link or ID with others so they can join you.
          </p>
          <div className="pt-2">
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
              }}
              className="py-2.5 px-5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-semibold transition shadow-lg shadow-blue-600/20"
            >
              Copy Meeting Link
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 5. MULTIPLE REMOTE PARTICIPANTS IN SPEAKER MODE
  if (layoutMode === 'speaker') {
    const activeSpeaker =
      remoteParticipants.find((p) => p.speaking) ||
      remoteParticipants[0];

    const secondaryParticipants = remoteParticipants.filter((p) => p.id !== activeSpeaker.id);

    return (
      <div className="flex-1 p-2 sm:p-4 h-full w-full overflow-hidden flex flex-col gap-2 sm:gap-3 min-h-0">
        {/* Main Stage */}
        <div className="flex-1 min-h-0 relative w-full h-full flex items-center justify-center">
          {activeSpeaker.speaking && (
            <div className="absolute top-3 sm:top-4 left-3 sm:left-4 z-20 flex items-center space-x-2 pointer-events-none">
              <span className="bg-emerald-600/90 backdrop-blur-md text-white text-[10px] sm:text-xs px-2 sm:px-2.5 py-1 rounded-lg flex items-center space-x-1.5 shadow-lg font-medium border border-emerald-400/30">
                <Volume2 className="w-3 sm:w-3.5 h-3 sm:h-3.5" />
                <span>Active Speaker</span>
              </span>
            </div>
          )}

          <VideoTile
            participant={activeSpeaker}
            isLocal={false}
            stream={activeSpeaker.stream}
            isPinned={pinnedId === activeSpeaker.id}
            onTogglePin={() => handleTogglePin(activeSpeaker.id)}
            className="w-full h-full shadow-2xl"
          />
        </div>

        {/* Filmstrip Carousel */}
        {secondaryParticipants.length > 0 && (
          <div className="h-24 sm:h-36 flex items-center gap-2 sm:gap-3 overflow-x-auto pb-1 flex-shrink-0">
            {secondaryParticipants.map((p) => (
              <div
                key={p.id}
                onClick={() => handleTogglePin(p.id)}
                className="w-36 sm:w-52 h-full flex-shrink-0 cursor-pointer hover:opacity-90 transition relative"
              >
                <VideoTile
                  participant={p}
                  isLocal={false}
                  stream={p.stream}
                  isPinned={pinnedId === p.id}
                  onTogglePin={() => handleTogglePin(p.id)}
                  className="w-full h-full"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // 6. MULTIPLE REMOTE PARTICIPANTS IN GRID MODE
  // Automatically fit height without scrolling:
  // On mobile (portrait): 2 participants stack vertically (grid-cols-1 sm:grid-cols-2) to fill the screen
  const count = remoteParticipants.length;
  let gridCols = 'grid-cols-1 sm:grid-cols-2';
  if (count >= 3 && count <= 4) gridCols = 'grid-cols-2';
  else if (count >= 5 && count <= 6) gridCols = 'grid-cols-2 sm:grid-cols-3';
  else if (count > 6) gridCols = 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4';

  return (
    <div className="flex-1 p-2 sm:p-4 h-full w-full overflow-hidden min-h-0">
      <div className={`grid ${gridCols} gap-2 sm:gap-3.5 h-full w-full auto-rows-fr`}>
        {remoteParticipants.map((p) => (
          <VideoTile
            key={p.id}
            participant={p}
            isLocal={false}
            stream={p.stream}
            isPinned={pinnedId === p.id}
            onTogglePin={() => handleTogglePin(p.id)}
            className="w-full h-full min-h-0"
          />
        ))}
      </div>
    </div>
  );
};
